function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useEffect, useMemo, memo } from 'react';
import { Box, TextInput, Text, FormField } from 'grommet';
import styled from 'styled-components';
import Edit from 'assets/Edit.svg';
import CurrencyInput from 'granite-admin/core/components/CurrencyInput';
// import PhoneInput from 'granite-admin/core/components/PhoneInput/v2';
import NumberInput from 'granite-admin/core/components/NumberInput';
const StyledText = styled(Text)`
  height: 100%;
  width: 100%;
  display: inline-block;
  text-overflow: ellipsis;
  overflow: hidden;
  &:hover {
    cursor: ${props => props.isEditable && `url(${Edit}), auto`};
  }
`;
const StyledBox = styled(Box)`
  cursor: inherit;
  &:focus {
    outline: none;
  }
`;
// If Edit mode is true renders an editable cell
// else return whatever data is passed
export const TextCell = /*#__PURE__*/memo(({
  data,
  initialValue = data,
  row,
  column: {
    isEditable = false,
    id,
    editProps = {},
    editType = 'text',
    saveOnEnter = false,
    stopPropagation = true
  },
  updateCellData,
  // This is a function that we supplied to our table instance
  editMode: isEditMode = false,
  columnId,
  addHandler,
  headers,
  haveEditPermission,
  width,
  type,
  onBlur: handleOnBlur,
  rowSaveLoader,
  ...rest
}) => {
  // We need to keep and update the state of the cell normally
  const tabIndex = useMemo(() => headers.map(h => h.id).indexOf(id) + row?.index, [headers, id, row]);
  const [value, setValue] = useState(initialValue);
  const initialEditMode = useMemo(() => (!row?.original?.pk || row?.original._type) && id !== 'pk' && (!addHandler?.colsId?.length || addHandler?.colsId.indexOf(id) > -1) && ! /*#__PURE__*/React.isValidElement(data) || isEditMode, [addHandler, data, id, isEditMode, row]);
  const [editMode, setEditMode] = useState(initialEditMode);
  const InputRef = React.useRef();
  const onChange = e => {
    let value = '';
    switch (editType) {
      case 'currency':
      case 'phone':
        value = e;
        break;
      default:
        value = e.target.value;
    }
    setValue(value);
  };

  // We'll only update the external data when the input is blurred
  const onBlur = e => {
    if (handleOnBlur) {
      handleOnBlur(e);
      return 1;
    }
    if (isEditable && !initialEditMode) setEditMode(false);
    if (value !== initialValue || row?.original?._type) {
      updateCellData(row.original, columnId || id, value);
    }
  };

  // If the initialValue is changed externally, sync it up with our state
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);
  useEffect(() => {
    setEditMode(initialEditMode);
  }, [initialEditMode]);
  let rowPk = row?.original?.pk;
  useEffect(() => {
    if (rowPk && isEditable && InputRef.current) InputRef.current.focus();
  }, [editMode, isEditable, rowPk]);

  // this line is commented as it was causing the errors to display as soon as new row is added.without even calling their onBlur.Now only first column will recieve focus
  const renderEditComponent = () => {
    let component = 'ok',
      otherProps = {
        size: 'xsmall',
        tabIndex,
        ref: InputRef,
        style: {
          width: width || 'auto'
        },
        onBlur,
        onClick: e => e.stopPropagation(),
        className: row?.original?._type || row?.original?.pk,
        disabled: rowSaveLoader?.[row?.original?._type],
        ...editProps,
        onKeyPress: event => {
          event.stopPropagation();
          if (event.key === 'Enter') {
            event.preventDefault();
            if (saveOnEnter) event.target.blur();
          }
          if (typeof editProps?.onKeyPress === 'function') editProps.onKeyPress(event);
        }
      };
    switch (editType) {
      case 'currency':
        component = /*#__PURE__*/React.createElement(CurrencyInput, _extends({
          autoFocus: true,
          value: value,
          onValueChange: onChange
        }, otherProps));
        break;
      // TODO: Buggy
      //1. country list dropdown position should be fixed
      //2. onBlur handling
      // case 'phone':
      //   component = (
      //     <PhoneInput inputProps={{ autoFocus: true, tabIndex }} value={value} onChange={onChange} {...otherProps} />
      //   );
      //   break;
      case 'number':
        component = /*#__PURE__*/React.createElement(FormField, {
          fill: true
        }, /*#__PURE__*/React.createElement(NumberInput, _extends({
          title: value,
          plain: true,
          value: value,
          onChange: onChange
        }, otherProps, {
          style: {
            background: 'inherit',
            padding: 0
          }
        })));
        break;
      default:
        component = /*#__PURE__*/React.createElement(FormField, {
          plain: true,
          fill: true
        }, /*#__PURE__*/React.createElement(TextInput, _extends({
          type: editType // support rest of html input types: text/email/password...
          ,
          value: value,
          onChange: onChange
        }, otherProps, {
          style: {
            background: 'inherit',
            padding: 0
          },
          title: value
        })));
    }
    return component;
  };
  //TODO: Find better way to handle uneven columns
  const getMaxHeight = () => {
    let errors = document.querySelectorAll('.' + row?.original?._type + '_error' || '.' + row?.original?.pk + '_error');
    // Math.max(...);
    let a = [];
    errors.forEach(i => {
      if (i.textContent) a.push(i?.getBoundingClientRect()?.height);
    });
    return a.length ? Math.max(...a) : 'auto';
  };
  return /*#__PURE__*/React.createElement(StyledBox, {
    onClick: e => {
      if ( /*#__PURE__*/React.isValidElement(data) && stopPropagation) e.stopPropagation();
    },
    focusIndicator: false,
    align: type === 'number' ? 'end' : 'start'
  }, editMode ? /*#__PURE__*/React.createElement(React.Fragment, null, renderEditComponent(), row?.original?.errors && /*#__PURE__*/React.createElement(Text, {
    className: row?.original?._type + '_error' || row?.original?.pk + '_error',
    as: "div",
    color: "status-critical",
    style: {
      minHeight: getMaxHeight(),
      whiteSpace: 'break-spaces'
    }
  }, row?.original?.errors?.[id] || row?.original?.errors?.[columnId])) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledText, _extends({
    onClick: isEditable ? e => {
      setEditMode(true);
      e.stopPropagation();
    } : undefined,
    isEditable: isEditable,
    title: String(data)?.length && !(typeof data === 'object' && !Array.isArray(data)) ? data : undefined
  }, rest), isEditable && !String(data)?.length ? '\u00A0' : data), row?.original?.errors && /*#__PURE__*/React.createElement(Text, {
    className: row?.original?._type + '_error' || row?.original?.pk + '_error',
    as: "div",
    color: "status-critical",
    style: {
      minHeight: getMaxHeight()
    }
  })));
});
TextCell.displayName = 'TextCell';